import React, {useState} from 'react';
import PropTypes from 'prop-types';
import Upload from 'rc-upload';
import './Uploader.scss';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import urljoin from 'url-join';
import addDocumentIcon from './addDocumentIcon.svg';
import Loader from './Loader/Loader';
import Translate from '../i18n/Translate/Translate';
import docIcon from '../assets/img/doc-icon.svg';
import {translate} from '../i18n/Translate';

const DASHBOARD_API = process.env.REACT_APP_API_BASE_URL || '';

const Uploader = (props) => {
  const {
    title, onSuccess, onError, uploadedImage, onRemove, id, maxSize, supportedTypes: supportedTypesProps, className
  } = props;
  const [isDragActive, setDragAction] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [image, setImage] = useState(uploadedImage);
  const [error, setError] = useState(null);
  const supportedTypes = supportedTypesProps.map(item => `.${item}`).join(', ')

  const handleSuccess = (data) => {
    setLoading(false);
    setDragAction(false);
    const isImg = data.url.match(/\.(jpeg|jpg|png)$/) != null;
    setImage(isImg ? data.url : docIcon);
    onSuccess(data);
  };

  const handleError = (a, error) => {
    setLoading(false);
    setDragAction(false);
    setImage(null);
    setError(error.statusCode === 413 ? 'File size too large.' : ((error && error.message) || 'Something went wrong'));
    onError(error);
  };

  const handleBeforeUpload = (file) => {
    const fileSize = (file.size / (1024 * 1024)).toFixed(2);
    if (fileSize > maxSize) {
      setError(translate('Errors.file-to-large', {maxSize, fileSize}));
      return false;
    }
    setError(null);
    setPercent(0);
    setImage(null);
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      setImage(null);
      onSuccess(null);
    }
  };

  return (
    <div className={`${className} uploader position-relative`} id={id}>
      <div className="uploader__title-wrapper">
        {
          !!title
          && (
            <span className="uploader__title font-14">
              <span className="d-flex align-items-start">
                <Translate translationKey={title}/>
              <button
                id={`${id}-delete`}
                className={classNames(
                  'ml-3 border-0 w-auto h-auto bg-white text-danger',
                  'uploader__remove-btn font-12 p-0',
                  {
                    'uploader__remove-btn--hide': !image,
                  },
                )}
                type="button"
                onClick={handleRemove}
              >
          <Translate translationKey="Texts.delete"/>
        </button>
              </span>
              <br/>
              <div className="uploader__title__supported">
                <span className="font-12 subtext">
                <Translate translationKey="Texts.supports"/>
                  {supportedTypes.toUpperCase()}
              </span>
              </div>
            </span>
          )
        }
      </div>
      <Upload
        id={`${id}-component`}
        action={urljoin(DASHBOARD_API, '/upload/temp')}
        headers={{Authorization: Cookies.get('auth-token')}}
        withCredentials
        beforeUpload={handleBeforeUpload}
        accept={supportedTypes}
        disabled={isLoading}
        onStart={() => setLoading(true)}
        onSuccess={handleSuccess}
        onError={handleError}
        onProgress={({percent}) => setPercent(percent)}
        component="div"
      >
        <div
          onDrop={() => setDragAction(false)}
          onDragEnter={() => setDragAction(true)}
          onDragOver={() => setDragAction(true)}
          onDragLeave={() => setDragAction(false)}
          className={classNames('uploader__drag-box', {
            'uploader__drag-box--active': isDragActive,
            'uploader__drag-box--disabled': isLoading,
            'uploader__drag-box--success': !!image,
            'uploader__drag-box--error': !!error,
          })}
        >
          {
            isLoading
              ? (<Loader percent={percent}/>)
              : (
                <div className="uploader__drag-box__add-document uploader__bg">
                  {
                    image
                      ? (
                        <img
                          className="uploader__drag-box__add-document__uploaded-image animated fadeIn"
                          alt="uploaded"
                          src={image}
                        />
                      )
                      : (
                        <>
                          <img src={addDocumentIcon} alt="add document"
                               className="uploader__drag-box__add-document__img--desktop mw-100 mh-100"
                          />
                          <div
                            className="uploader__drag-box__add-document__img--mobile mw-100 mh-100">
                            <span>+</span>
                          </div>
                          <span className="uploader__drag-box__add-document__supported font-12">
                            <Translate translationKey="Texts.supports"/>
                            {' '}
                            {supportedTypes.toUpperCase()}
                          </span>
                          <span className="uploader__drag-box__add-document__browse font-12">
                            <Translate translationKey="Texts.browse"/>
                          </span>
                        </>
                      )
                  }
                </div>
              )
          }
        </div>
      </Upload>
      {
        error && (
          <div className="uploader__error font-12">
            <Translate translationKey={error}/>
          </div>
        )
      }
    </div>
  );
};

Uploader.propTypes = {
  title: PropTypes.string,
  uploadedImage: PropTypes.string,
  supportedTypes: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  className: PropTypes.string,
  maxSize: PropTypes.number,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  onRemove: PropTypes.func,
};

Uploader.defaultProps = {
  onError: () => undefined,
  onSuccess: () => undefined,
  maxSize: 8,
  onRemove: null,
  title: null,
  uploadedImage: null,
  supportedTypes: ['jpg', 'png', 'pdf'],
  id: '',
  className: '',
};

export default Uploader;
